import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import { DailyService } from '../daily.service';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import {
  mobilityEnergySource,
  mobilityVehicle,
  userMobilityEntry,
} from 'src/app/shared/models/interfaces';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { isSameDay } from 'src/app/shared/utils/date';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'app-mobility',
  imports: [
    UIComponentsModule,
    MatTooltipModule,
    MatInputModule,
    FormsModule,
    MatMenuModule,
  ],
  templateUrl: './mobility.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./mobility.component.scss'],
})
export class MobilityComponent implements OnInit, OnChanges {
  constructor(
    private dailyService: DailyService,
    private snackbarService: SnackbarService
  ) {}

  userMobilityEntries: userMobilityEntry[] = [];
  mobilityVehicles: mobilityVehicle[] = [];
  selectedVehicle: mobilityVehicle | null = null;
  selectedEnergySource: any = null;
  showEnergySourcePopup: boolean = false;

  @Input() selectedDate!: Date;

  // Used to navigate to next daily entry type
  @Output() continueToNext = new EventEmitter<void>();

  distanceValue: string = '';

  ngOnInit(): void {
    this.loadUserMobilityData();
    this.loadMobilityVehicles();
  }

  private loadMobilityVehicles() {
    this.dailyService.getMobilityData();
    this.dailyService.mobilityData$.subscribe((vehicles) => {
      this.mobilityVehicles = vehicles;
      this.mapVehiclesToIcons();
      if (vehicles.length > 0 && !this.selectedVehicle) {
        this.selectVehicle(vehicles[0]);
      }
    });
  }

  private loadUserMobilityData() {
    this.dailyService.getUserMobilityEntries();
    this.dailyService.userMobilityEntries$.subscribe((entries) => {
      this.userMobilityEntries = entries;
    });
  }

  ngOnChanges(): void {
    if (
      this.mobilityVehicles &&
      this.mobilityVehicles.length > 0 &&
      !this.selectedVehicle
    ) {
      this.selectedVehicle = this.mobilityVehicles[0];
    }
  }

  private get filteredUserMobilityEntries(): userMobilityEntry[] {
    return this.userMobilityEntries.filter((entry) =>
      isSameDay(entry.occurredAt, this.selectedDate)
    );
  }

  selectVehicle(vehicle: mobilityVehicle) {
    this.selectedVehicle = vehicle;

    // Look for existing entries for this vehicle to determine default energy source
    const vehicleEmissionIds = vehicle.energySources.map(
      (energySource) => energySource.emissionId
    );
    const vehicleEntries = this.filteredUserMobilityEntries.filter((entry) =>
      vehicleEmissionIds.includes(entry.mobilityEmissionId)
    );

    if (vehicleEntries.length > 0) {
      // Find the most recent entry (last in the array)
      const mostRecentEntry = vehicleEntries[vehicleEntries.length - 1];
      const lastUsedEnergySource = vehicle.energySources.find(
        (energySource) =>
          energySource.emissionId === mostRecentEntry.mobilityEmissionId
      );
      this.selectedEnergySource =
        lastUsedEnergySource || vehicle.energySources[0];
    } else {
      // No entries found, select first energy source
      this.selectedEnergySource =
        vehicle.energySources && vehicle.energySources.length > 0
          ? vehicle.energySources[0]
          : null;
    }
  }

  openEnergySourcePopup() {
    this.showEnergySourcePopup = true;
  }

  closeEnergySourcePopup() {
    this.showEnergySourcePopup = false;
  }

  selectEnergySource(energySource: mobilityEnergySource) {
    this.selectedEnergySource = energySource;
    this.closeEnergySourcePopup();
  }

  addEntry(distance: string) {
    // Convert distance to a number, if it's not a valid number, return early
    const parsedDistance = parseFloat(distance);
    if (isNaN(parsedDistance)) {
      this.snackbarService.showErrorMessage('Please enter a valid distance.');
      return;
    }
    if (
      !this.selectedVehicle ||
      !this.selectedEnergySource ||
      !parsedDistance ||
      parsedDistance <= 0
    ) {
      return;
    }

    const newEntry: userMobilityEntry = {
      mobilityEmissionId: this.selectedEnergySource.emissionId,
      distance: parsedDistance,
      occurredAt: this.selectedDate,
    };

    this.dailyService.createUserMobilityEntry(newEntry).subscribe({
      next: () => {
        // Clear the input field after successful entry
        this.distanceValue = '';
      },
      error: (err) => {
        console.error('Error creating mobility entry:', err);
        this.snackbarService.showErrorMessage(
          'Failed to create mobility entry. Please try again.'
        );
      },
    });
  }

  removeEntry(entry: userMobilityEntry) {
    if (!entry.id) {
      // Entry doesn't have an ID, just remove from local array
      const index = this.userMobilityEntries.findIndex(
        (e) =>
          e.mobilityEmissionId === entry.mobilityEmissionId &&
          e.distance === entry.distance &&
          isSameDay(e.occurredAt, entry.occurredAt)
      );
      if (index !== -1) {
        this.userMobilityEntries.splice(index, 1);
      }
      return;
    }

    this.dailyService.deleteUserMobilityEntry(entry.id).subscribe({
      next: () => {
        const index = this.userMobilityEntries.findIndex(
          (e) => e.id === entry.id
        );
        if (index !== -1) {
          this.userMobilityEntries.splice(index, 1);
        }
      },
      error: (err) => {
        console.error('Error deleting mobility entry:', err);
        this.snackbarService.showErrorMessage(
          'Failed to delete mobility entry. Please try again.'
        );
      },
    });
  }

  getEntriesForVehicle(vehicle: mobilityVehicle): userMobilityEntry[] {
    const emissionIds = vehicle.energySources.map((es) => es.emissionId);
    return this.filteredUserMobilityEntries.filter((entry) =>
      emissionIds.includes(entry.mobilityEmissionId)
    );
  }

  getNextVehicleIcon(vehicle: mobilityVehicle): string {
    const currentIndex = this.mobilityVehicles.findIndex(
      (v) => v.id === vehicle.id
    );
    const nextVehicle = this.mobilityVehicles[currentIndex + 1];
    return nextVehicle ? nextVehicle.icon || 'car' : 'arrow_right';
  }

  hasEntries(vehicle: mobilityVehicle): boolean {
    return vehicle.energySources.some((energySource) =>
      this.filteredUserMobilityEntries.some(
        (entry) =>
          entry.mobilityEmissionId === energySource.emissionId &&
          entry.distance > 0
      )
    );
  }

  hasMultipleEnergySources(): boolean {
    return !!(
      this.selectedVehicle &&
      this.selectedVehicle.energySources &&
      this.selectedVehicle.energySources.length > 1
    );
  }

  getEnergySourceText(mobilityEmissionId: number): string {
    for (const vehicle of this.mobilityVehicles) {
      const energySource = vehicle.energySources.find(
        (es) => es.emissionId === mobilityEmissionId
      );
      if (energySource) {
        return energySource.text.text_en || energySource.text.text_de;
      }
    }
    return '';
  }

  onContinue() {
    const currentIndex = this.mobilityVehicles.findIndex(
      (vehicle) => vehicle.id === this.selectedVehicle?.id
    );
    if (
      this.selectedVehicle &&
      currentIndex < this.mobilityVehicles.length - 1
    ) {
      this.selectVehicle(this.mobilityVehicles[currentIndex + 1]);
    } else {
      // Last vehicle, emit event to parent to switch to next tab
      this.continueToNext.emit();
    }
  }

  private mapVehiclesToIcons() {
    this.mobilityVehicles = this.mobilityVehicles.map((vehicle) => {
      const vehicleKey = vehicle.text.text_de || vehicle.text.text_en;
      return {
        ...vehicle,
        icon: mobilityIcons[vehicleKey] || 'question_mark', // Default to 'question_mark' if no icon is found
      };
    });
  }
}

const mobilityIcons: { [key: string]: string } = {
  /* The keys include the english and german names for the different vehicles to map the corresponding icon accordingly. */
  Auto: 'car',
  Car: 'car',
  Trotinette: 'electric_scooter',
  'Scooter/Trottinett': 'electric_scooter',
  Fahrrad: 'bicycle',
  Bicycle: 'bicycle',
  'E-Bike': 'electric_bicycle',
  Motorrad: 'motorcycle',
  Motorcycle: 'motorcycle',
  Reisebus: 'coach',
  Coach: 'coach',
  Stadtbus: 'bus',
  'City bus': 'bus',
  Tram: 'tram',
  Eisenbahn: 'train',
  Railway: 'train',
};
